import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, TextInput, Pressable, Modal, ScrollView } from '../../lib/react-native-compat';
import {
    dataSourceJobsAtom, selectedJobAtom, selectedJobDataAtom, selectedTeamAtom,
    jobOpenCommitmentsAtom, jobUserDataAtom, jobCostDetailAtom, jobCostDetailCondensedAtom,
    k2DataAtom, jobHEETDataAtom, phasedForecastAtom,
} from '../../store/company';
import {
    setSelectedJobStorage, setSelectedJobDetailsStorage, setSelectedTeamStorage, setClosedJobMode,
} from '../../db/company';

// Team/job switcher for the Daily Field Report header. The standalone
// /daily-report route hides the top menu, so this is the only way to change
// jobs there. Selecting a job mirrors the topMenu flow: clear the per-job
// caches, commit team + job to the atoms and storage, and let LoadData
// refetch phase codes / commitments for the new job.

const TEAM_NAMES = {
    NOR: 'Northern',
    SWT: 'Southwest',
    EASTERN: 'Eastern',
    MCLEANS: 'Mcleans',
    'GULF COAST': 'Gulf Coast',
    PICKETT: 'Pickett',
    ETX: 'ETX',
};

const JobSwitcher = ({ variant = 'button', textStyle, onBeforeSwitch }) => {
    const [jobs] = useAtom(dataSourceJobsAtom);
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [jobData, setSelectedJobData] = useAtom(selectedJobDataAtom);
    const [selectedTeam, setSelectedTeam] = useAtom(selectedTeamAtom);
    const [, setJobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [, setJobUserData] = useAtom(jobUserDataAtom);
    const [, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [, setJobCostDetailCondensed] = useAtom(jobCostDetailCondensedAtom);
    const [, setK2Data] = useAtom(k2DataAtom);
    const [, setJobHEETData] = useAtom(jobHEETDataAtom);
    const [, setPhased] = useAtom(phasedForecastAtom);

    const [open, setOpen] = useState(false);
    const [activeTeam, setActiveTeam] = useState(null);
    const [search, setSearch] = useState('');

    const teams = useMemo(() => {
        const codes = new Set();
        (jobs || []).forEach((j) => { if (j?.Cat_Code_BU_27) codes.add(j.Cat_Code_BU_27); });
        return [...codes].sort();
    }, [jobs]);

    const teamJobs = useMemo(() => {
        if (!activeTeam) return [];
        let list = (jobs || []).filter((j) => j?.Cat_Code_BU_27 === activeTeam);
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
                (j) => j.BU_ID?.toLowerCase().includes(q) || j.BU_Description?.toLowerCase().includes(q),
            );
        }
        return list;
    }, [jobs, activeTeam, search]);

    const openPicker = () => {
        setActiveTeam(selectedTeam || jobData?.Cat_Code_BU_27 || null);
        setSearch('');
        setOpen(true);
    };

    const pickJob = (job) => {
        if (job.BU_ID === selectedJob) { setOpen(false); return; }
        if (onBeforeSwitch) onBeforeSwitch();
        // Same cache clearing topMenu does on a job change — LoadData refetches.
        setJobOpenCommitments([]);
        setJobUserData([]);
        setJobCostDetail([]);
        setJobCostDetailCondensed([]);
        setK2Data(null);
        setJobHEETData(null);
        setClosedJobMode(false);
        const team = job.Cat_Code_BU_27 || activeTeam;
        if (team) {
            setSelectedTeam(team);
            setSelectedTeamStorage(team);
        }
        setSelectedJob(job.BU_ID);
        setSelectedJobData(job);
        setSelectedJobDetailsStorage(job);
        setSelectedJobStorage(job.BU_ID);
        setPhased(job.Cat_Code_BU_18 == '3' ? 'phased' : 'regular');
        setOpen(false);
    };

    const jobLabel = selectedJob
        ? [selectedJob, jobData?.BU_Description || jobData?.Job_Description].filter(Boolean).join(' — ')
        : 'Select a job';

    const trigger = variant === 'line' ? (
        <Pressable onPress={openPicker} style={{ cursor: 'pointer', maxWidth: '100%' }}>
            <Text
                numberOfLines={1}
                style={{
                    fontSize: 11, color: '#334155', fontWeight: '600', maxWidth: '100%',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center',
                    ...textStyle,
                }}
            >
                {jobLabel}{' ▾'}
            </Text>
        </Pressable>
    ) : (
        <Pressable
            onPress={openPicker}
            style={{
                flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start',
                minHeight: 40, borderRadius: 8, border: '1px solid #CBD5E1', backgroundColor: '#fff',
                paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, cursor: 'pointer',
                maxWidth: '100%',
            }}
        >
            <Text
                numberOfLines={1}
                style={{
                    fontSize: 13, fontWeight: '600', color: '#0F172A',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    ...textStyle,
                }}
            >
                {jobLabel}
            </Text>
            <Text style={{ fontSize: 11, color: '#64748B', marginLeft: 6 }}>▾</Text>
        </Pressable>
    );

    return (
        <>
            {trigger}
            <Modal
                visible={open}
                transparent={false}
                onRequestClose={() => setOpen(false)}
                style={{ width: 560, maxWidth: '94vw', maxHeight: '86vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottom: '1px solid #E2E8F0' }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>Switch Job</Text>
                    <Pressable onPress={() => setOpen(false)} style={{ minHeight: 32, minWidth: 32, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <Text style={{ fontSize: 16, color: '#64748B' }}>✕</Text>
                    </Pressable>
                </View>

                {!jobs || jobs.length === 0 ? (
                    <View style={{ padding: 24, alignItems: 'center' }}>
                        <Text style={{ fontSize: 13, color: '#64748B' }}>Job list is still loading…</Text>
                    </View>
                ) : (
                    <>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, padding: 12, paddingBottom: 0 }}>
                            {teams.map((code) => (
                                <Pressable
                                    key={code}
                                    onPress={() => setActiveTeam(code)}
                                    style={{
                                        minHeight: 36, borderRadius: 999, alignItems: 'center', justifyContent: 'center',
                                        paddingTop: 6, paddingBottom: 6, paddingLeft: 14, paddingRight: 14, cursor: 'pointer',
                                        backgroundColor: activeTeam === code ? '#1D4ED8' : '#F1F5F9',
                                        border: activeTeam === code ? '1px solid #1D4ED8' : '1px solid #E2E8F0',
                                    }}
                                >
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: activeTeam === code ? '#fff' : '#475569' }}>
                                        {TEAM_NAMES[code] || code}
                                    </Text>
                                </Pressable>
                            ))}
                        </View>

                        <View style={{ padding: 12, paddingBottom: 8 }}>
                            <TextInput
                                value={search}
                                onChangeText={setSearch}
                                placeholder="Search jobs"
                                style={{ padding: 10, borderRadius: 8, border: '1px solid #CBD5E1', width: '100%', fontSize: 13 }}
                            />
                        </View>

                        <ScrollView style={{ maxHeight: '48vh' }}>
                            <View style={{ paddingLeft: 12, paddingRight: 12, paddingBottom: 12 }}>
                                {!activeTeam && (
                                    <Text style={{ fontSize: 13, color: '#64748B', padding: 8 }}>Pick a team to see its jobs.</Text>
                                )}
                                {activeTeam && teamJobs.length === 0 && (
                                    <Text style={{ fontSize: 13, color: '#64748B', padding: 8 }}>No jobs match.</Text>
                                )}
                                {teamJobs.map((job) => {
                                    const current = job.BU_ID === selectedJob;
                                    return (
                                        <Pressable
                                            key={job.BU_ID}
                                            onPress={() => pickJob(job)}
                                            style={{
                                                flexDirection: 'row', alignItems: 'center', minHeight: 44,
                                                borderRadius: 8, paddingLeft: 10, paddingRight: 10, marginBottom: 4, cursor: 'pointer',
                                                backgroundColor: current ? '#EFF6FF' : 'transparent',
                                                border: current ? '1px solid #BFDBFE' : '1px solid transparent',
                                            }}
                                        >
                                            <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A', minWidth: 70, flexShrink: 0, marginRight: 12 }}>{job.BU_ID}</Text>
                                            <Text
                                                numberOfLines={1}
                                                style={{ fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                                            >
                                                {job.BU_Description}
                                            </Text>
                                            {current && <Text style={{ fontSize: 11, fontWeight: '700', color: '#1D4ED8', marginLeft: 8 }}>Current</Text>}
                                        </Pressable>
                                    );
                                })}
                            </View>
                        </ScrollView>
                    </>
                )}
            </Modal>
        </>
    );
};

export default JobSwitcher;
